import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import { apiPost } from '../../utils/apiClient';

// ─── Design tokens ────────────────────────────────────────────────────────────
const BRAND      = '#5A38FD';
const GREEN      = '#10B981';
const AMBER      = '#F59E0B';
const RED        = '#EF4444';

const DEAL_COLORS = {
  'Brand Partnership':   '#a78bfa',
  'Social Content':      '#818cf8',
  'Event Appearance':    '#c4b5fd',
  'Product Endorsement': '#9333ea',
  'Coaching':            '#7c6cfc',
  'Other':               'rgba(255,255,255,0.25)',
};
const FALLBACK_COLORS = ['#a78bfa','#818cf8','#c4b5fd','#9333ea','#7c6cfc','rgba(255,255,255,0.25)'];

// ─── Custom tooltip ───────────────────────────────────────────────────────────
const EarningsTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--surface-3)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '10px 14px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.72rem', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
      <p style={{ color: GREEN, fontSize: '1rem', fontWeight: 700, margin: 0 }}>${(payload[0].value || 0).toLocaleString()}</p>
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: 'var(--surface-3)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '10px 14px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.72rem', margin: '0 0 4px' }}>{d.label}</p>
      <p style={{ color: DEAL_COLORS[d.label] || BRAND, fontSize: '0.95rem', fontWeight: 700, margin: '0 0 2px' }}>${d.value.toLocaleString()}</p>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', margin: 0 }}>{d.count} deal{d.count !== 1 ? 's' : ''}</p>
    </div>
  );
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, subColor = 'var(--text-muted)', iconBg, iconEl, borderColor = 'var(--border-subtle)', bg = 'var(--surface-2)' }) => (
  <div style={{ background: bg, border: `1px solid ${borderColor}`, borderRadius: 16, padding: '1.25rem', transition: 'transform 0.2s', cursor: 'default' }}
    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
    onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      <div style={{ width: 36, height: 36, borderRadius: 10, background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {iconEl}
      </div>
    </div>
    <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-primary)', marginBottom: 6, letterSpacing: '-0.02em' }}>{value}</div>
    <div style={{ fontSize: '0.78rem', fontWeight: 500, color: subColor }}>{sub}</div>
  </div>
);

// ─── SVG icons (inline, no heroicons dep for this file) ──────────────────────
const IcoUp    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>;
const IcoDown  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>;
const IcoFlat  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IcoStar  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>;
const IcoLight = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/><circle cx="12" cy="12" r="4"/></svg>;
const IcoTrend = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>;
const IcoSparkle = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L14.4 9.6H22L15.8 14.4L18.2 22L12 17.2L5.8 22L8.2 14.4L2 9.6H9.6L12 2Z"/></svg>;

// ─── Responsive styles ────────────────────────────────────────────────────────
const responsiveStyles = `
  @media (max-width: 600px) {
    .ea-chart-grid  { grid-template-columns: 1fr !important; }
    .ea-stat-grid   { grid-template-columns: 1fr !important; }
    .ea-header      { flex-direction: column !important; align-items: flex-start !important; }
  }
`;

// ─── Main Component ───────────────────────────────────────────────────────────
export const EarningsAnalytics = ({ deals = [], totalEarnings = 0, monthlyEarnings = 0 }) => {
  const [timeRange, setTimeRange] = useState('6m');
  const [isLoading, setIsLoading] = useState(true);
  const [aiInsights, setAiInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(null);

  const cleanAiText = (text) => {
    if (!text) return text;
    const patterns = [/\*\*KEY OPPORTUNITY:\*\*/gi,/\*\*STRATEGIC RECOMMENDATION:\*\*/gi,/\*\*CURRENT PERFORMANCE:\*\*/gi,/\*\*RECOMMENDATION:\*\*/gi,/\*\*OPPORTUNITY:\*\*/gi,/\*\*SUMMARY:\*\*/gi,/\*\*INSIGHT:\*\*/gi];
    let t = text;
    patterns.forEach(p => { t = t.replace(p, ''); });
    return t.replace(/^\s+/g, '').trim();
  };

  const generateAIInsights = async (earningsData, dealData, metrics) => {
    if (insightsLoading) return;
    setInsightsLoading(true);
    try {
      const prompt = `You are an AI financial advisor specializing in NIL earnings for student athletes. Analyze the following data and provide insights in plain, encouraging language.\n\nTotal: $${totalEarnings.toLocaleString()}, Monthly: $${monthlyEarnings.toLocaleString()}, Deals: ${deals.length}, Avg: $${metrics.avgDealValue.toLocaleString()}, Growth: ${metrics.growth.direction === 'up' ? '+' : metrics.growth.direction === 'down' ? '-' : ''}${metrics.growth.rate.toFixed(1)}%, Period: ${timeRange}\n\nProvide:\n1. 2-3 sentence performance summary\n2. One specific forward-looking recommendation\n3. One risk or opportunity\n\nKeep tone positive, professional, actionable. No repeated headers in response text.`;
      const data = await apiPost('/api/earnings/ai-insights', { prompt });
      if (data.success && data.insights) {
        setAiInsights({ summary: cleanAiText(data.insights.summary), recommendation: cleanAiText(data.insights.recommendation), opportunity: cleanAiText(data.insights.opportunity) });
      } else { setAiInsights(generateFallbackInsights(metrics)); }
    } catch { setAiInsights(generateFallbackInsights(metrics)); }
    finally { setInsightsLoading(false); }
  };

  const generateFallbackInsights = ({ growth, avgDealValue }) => ({
    summary: `Your NIL earnings show ${growth.direction === 'up' ? 'positive momentum' : growth.direction === 'down' ? 'recent challenges' : 'stable performance'} with $${monthlyEarnings.toLocaleString()} earned this month across ${deals.length} active deals.`,
    recommendation: growth.direction === 'up' ? "Leverage your momentum to negotiate higher-value deals and explore new brand partnerships." : "Diversify your deal portfolio and maintain social media engagement to attract new opportunities.",
    opportunity: avgDealValue > 5000 ? "Your above-average deal values signal strong market positioning — consider creating premium partnership tiers." : "Opportunity exists to increase average deal value through better negotiation and targeting higher-tier brands.",
  });

  useEffect(() => {
    const t = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(t);
  }, []);

  const processEarningsData = () => {
    const months = { '3m': 3, '6m': 6, '12m': 12 }[timeRange] || 6;
    if (!deals || deals.length === 0) {
      const base = 4500, growth = timeRange === '12m' ? 1.9 : timeRange === '6m' ? 1.5 : 1.25;
      return Array.from({ length: months }, (_, i) => {
        const d = new Date(); d.setMonth(d.getMonth() - (months - 1 - i));
        const progress = i / (months - 1);
        const value = Math.floor(base * (1 + progress * (growth - 1)) * (1 + (Math.random() - 0.45) * 0.25));
        return { label: d.toLocaleDateString('en-US', { month: 'short' }), value: Math.max(value, 800), date: d.toISOString().split('T')[0] };
      });
    }
    const map = {};
    deals.forEach(deal => {
      if (deal.deal_date && deal.deal_value) {
        const d = new Date(deal.deal_date);
        const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2,'0')}`;
        if (!map[key]) map[key] = { label: d.toLocaleDateString('en-US', { month: 'short' }), value: 0, date: d.toISOString().split('T')[0] };
        map[key].value += parseFloat(deal.deal_value) || 0;
      }
    });
    return Object.values(map).sort((a,b) => new Date(a.date)-new Date(b.date)).slice(-months);
  };

  const generateDealTypeData = () => {
    const cats = ['Brand Partnership','Social Content','Event Appearance','Product Endorsement','Coaching','Other'];
    if (!deals || deals.length === 0) return [
      { label: 'Brand Partnership', value: 18000, count: 4 },
      { label: 'Social Content',    value: 12000, count: 8 },
      { label: 'Event Appearance',  value:  8000, count: 3 },
      { label: 'Product Endorsement', value: 6000, count: 5 },
    ];
    const map = {};
    deals.forEach(deal => {
      let type = deal.deal_type || deal.category || 'Brand Partnership';
      if (!cats.includes(type)) {
        if (type.toLowerCase().includes('brand')) type = 'Brand Partnership';
        else if (type.toLowerCase().includes('social')) type = 'Social Content';
        else if (type.toLowerCase().includes('event')) type = 'Event Appearance';
        else if (type.toLowerCase().includes('product')) type = 'Product Endorsement';
        else if (type.toLowerCase().includes('coaching')) type = 'Coaching';
        else type = 'Other';
      }
      const val = parseFloat(deal.deal_value) || 0;
      if (!map[type]) map[type] = { label: type, value: 0, count: 0 };
      map[type].value += val; map[type].count += 1;
    });
    return Object.values(map).sort((a,b) => b.value - a.value).slice(0, 6);
  };

  const calculateGrowth = () => {
    const data = processEarningsData();
    if (data.length < 2) return { rate: 0, direction: 'neutral' };
    const curr = data[data.length-1].value, prev = data[data.length-2].value;
    const rate = ((curr - prev) / (prev || 1)) * 100;
    return { rate: Math.abs(rate), direction: rate > 0 ? 'up' : rate < 0 ? 'down' : 'neutral' };
  };

  const growth      = calculateGrowth();
  const earningsData = processEarningsData();
  const dealTypeData = generateDealTypeData();
  const avgDealValue = deals.length > 0 ? totalEarnings / deals.length : totalEarnings > 0 ? totalEarnings / 3 : 7300;
  const totalDealCount = dealTypeData.reduce((s, d) => s + d.count, 0);

  useEffect(() => {
    if (!isLoading && earningsData.length > 0) generateAIInsights(earningsData, dealTypeData, { growth, avgDealValue });
  }, [timeRange, totalEarnings, monthlyEarnings, deals.length, isLoading]);

  // ─── Loading skeleton ────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ height: 28, background: 'var(--surface-4)', borderRadius: 6, width: 200 }} />
          <div style={{ height: 38, background: 'var(--surface-4)', borderRadius: 10, width: 260 }} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          {[0,1].map(i => (
            <div key={i} style={{ background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '1.25rem' }}>
              <div style={{ height: 12, background: 'var(--surface-4)', borderRadius: 4, width: '50%', marginBottom: 16 }} />
              <div style={{ height: 36, background: 'var(--surface-4)', borderRadius: 6, width: '60%', marginBottom: 10 }} />
              <div style={{ height: 10, background: 'var(--surface-4)', borderRadius: 4, width: '70%' }} />
            </div>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>
          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '1.5rem', height: 320 }}>
            <div style={{ height: 18, background: 'var(--surface-4)', borderRadius: 4, width: 140, marginBottom: 24 }} />
            <div style={{ height: 240, background: 'var(--surface-4)', borderRadius: 8 }} />
          </div>
          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '1.5rem', height: 320 }}>
            <div style={{ height: 18, background: 'var(--surface-4)', borderRadius: 4, width: 120, marginBottom: 24 }} />
            <div style={{ height: 240, background: 'var(--surface-4)', borderRadius: '50%', width: 180, margin: '0 auto' }} />
          </div>
        </div>
      </div>
    );
  }

  const growthColor = growth.direction === 'up' ? GREEN : growth.direction === 'down' ? RED : 'var(--text-muted)';
  const fmt = (n) => `$${(n || 0).toLocaleString()}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{responsiveStyles}</style>

      {/* ── Header ────────────────────────────────────────────────────────────── */}
      <div className="ea-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Earnings Analytics</h2>
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: '2px 0 0' }}>Track your NIL performance and growth</p>
        </div>
        <div style={{ display: 'flex', background: 'var(--surface-3)', borderRadius: 10, padding: 4, border: '1px solid var(--border-subtle)', gap: 2 }}>
          {[{ key: '3m', label: '3M' }, { key: '6m', label: '6M' }, { key: '12m', label: '1Y' }].map(({ key, label }) => (
            <button key={key} onClick={() => setTimeRange(key)}
              style={{ padding: '5px 14px', borderRadius: 7, fontSize: '0.78rem', fontWeight: 700, border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                background: timeRange === key ? 'linear-gradient(135deg,#5A38FD,#7F00FF)' : 'transparent',
                color: timeRange === key ? '#fff' : 'var(--text-secondary)',
                boxShadow: timeRange === key ? '0 2px 10px rgba(90,56,253,0.35)' : 'none',
              }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Stat cards ────────────────────────────────────────────────────────── */}
      <div className="ea-stat-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <StatCard
          label="This Month"
          value={fmt(monthlyEarnings)}
          sub={<span style={{ display: 'flex', alignItems: 'center', gap: 4, color: growthColor }}>
            {growth.direction === 'up' ? <IcoUp /> : growth.direction === 'down' ? <IcoDown /> : <IcoFlat />}
            {growth.rate.toFixed(1)}% vs last month
          </span>}
          iconBg={growth.direction === 'up' ? 'rgba(16,185,129,0.15)' : growth.direction === 'down' ? 'rgba(239,68,68,0.15)' : 'var(--surface-3)'}
          iconEl={<span style={{ color: growthColor }}><IcoTrend /></span>}
          borderColor={growth.direction === 'up' ? 'rgba(16,185,129,0.2)' : growth.direction === 'down' ? 'rgba(239,68,68,0.2)' : 'var(--border-subtle)'}
          bg={growth.direction === 'up' ? 'rgba(16,185,129,0.05)' : growth.direction === 'down' ? 'rgba(239,68,68,0.05)' : 'var(--surface-2)'}
        />
        <StatCard
          label="Avg Per Deal"
          value={fmt(Math.round(avgDealValue))}
          sub={<span style={{ display: 'flex', alignItems: 'center', gap: 4, color: BRAND }}><IcoStar />{deals.length || '—'} active deal{deals.length !== 1 ? 's' : ''}</span>}
          iconBg="rgba(90,56,253,0.15)"
          iconEl={<span style={{ color: BRAND }}><IcoStar /></span>}
          borderColor="rgba(90,56,253,0.2)"
          bg="rgba(90,56,253,0.05)"
        />
      </div>

      {/* ── Charts ────────────────────────────────────────────────────────────── */}
      <div className="ea-chart-grid" style={{ display: 'grid', gridTemplateColumns: '1.45fr 1fr', gap: 14, alignItems: 'start' }}>

        {/* Area chart — earnings trend */}
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '1.25rem 1rem 1rem 0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingLeft: '0.75rem' }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(16,185,129,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: GREEN }}>
              <IcoTrend />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>Earnings Trend</p>
              <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--text-muted)' }}>Monthly earnings over time</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={earningsData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={BRAND} stopOpacity={0.35} />
                  <stop offset="85%"  stopColor={BRAND} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%"  stopColor={BRAND} />
                  <stop offset="100%" stopColor={GREEN} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 500 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }} axisLine={false} tickLine={false} width={46} />
              <Tooltip content={<EarningsTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.08)', strokeWidth: 1 }} />
              <Area type="monotone" dataKey="value" stroke="url(#lineGrad)" strokeWidth={2.5} fill="url(#areaGrad)" dot={false} activeDot={{ r: 5, fill: GREEN, stroke: '#fff', strokeWidth: 2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Donut — deal breakdown */}
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(90,56,253,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: BRAND }}>
              <IcoStar />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>Deal Mix</p>
              <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--text-muted)' }}>By category</p>
            </div>
          </div>

          {/* Donut chart with center label */}
          <div style={{ position: 'relative', height: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={dealTypeData}
                  dataKey="value"
                  nameKey="label"
                  innerRadius={58}
                  outerRadius={82}
                  paddingAngle={3}
                  strokeWidth={0}
                  onMouseEnter={(_, idx) => setActiveIndex(idx)}
                  onMouseLeave={() => setActiveIndex(null)}
                >
                  {dealTypeData.map((entry, idx) => (
                    <Cell
                      key={idx}
                      fill={DEAL_COLORS[entry.label] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length]}
                      opacity={activeIndex === null || activeIndex === idx ? 1 : 0.45}
                      style={{ transition: 'opacity 0.2s' }}
                    />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {/* Center label */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <span style={{ fontSize: '1.375rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>{totalDealCount}</span>
              <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: 3 }}>deals</span>
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginTop: 14 }}>
            {dealTypeData.map((d, idx) => {
              const color = DEAL_COLORS[d.label] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
              const pct = totalEarnings > 0 ? ((d.value / totalEarnings) * 100).toFixed(0) : 0;
              return (
                <div key={d.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: color }} />
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
                  </div>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Bar chart — deal count by type ───────────────────────────────────── */}
      {dealTypeData.length > 1 && (
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: '1.25rem 1rem 1rem 0.5rem' }}>
          <div style={{ paddingLeft: '0.75rem', marginBottom: 14 }}>
            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>Revenue by Category</p>
            <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: 'var(--text-muted)' }}>Earnings value per deal type</p>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={dealTypeData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }} barSize={22}>
              <CartesianGrid strokeDasharray="3 6" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
              <YAxis tickFormatter={v => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }} axisLine={false} tickLine={false} width={44} />
              <Tooltip content={<EarningsTooltip />} cursor={{ fill: 'rgba(255,255,255,0.03)' }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {dealTypeData.map((entry, idx) => (
                  <Cell key={idx} fill={DEAL_COLORS[entry.label] || FALLBACK_COLORS[idx % FALLBACK_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── AI Insights ───────────────────────────────────────────────────────── */}
      <div style={{ background: 'rgba(90,56,253,0.06)', border: '1px solid rgba(90,56,253,0.18)', borderRadius: 16, padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,#5A38FD,#7F00FF)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0 }}>
            <IcoSparkle />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>AI Performance Insights</p>
            <p style={{ margin: 0, fontSize: '0.72rem', color: 'var(--text-muted)' }}>Powered by AI — updated each session</p>
          </div>
        </div>

        {insightsLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 16, height: 16, border: `2px solid ${BRAND}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Analyzing your earnings…</span>
            </div>
            <div style={{ height: 13, background: 'var(--surface-4)', borderRadius: 4, width: '75%' }} />
            <div style={{ height: 13, background: 'var(--surface-4)', borderRadius: 4, width: '55%' }} />
            <div style={{ height: 13, background: 'var(--surface-4)', borderRadius: 4, width: '65%' }} />
          </div>
        ) : aiInsights ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { icon: <IcoLight />, color: AMBER, label: 'Performance', text: aiInsights.summary },
              { icon: <IcoTrend />, color: GREEN,  label: 'Recommendation', text: aiInsights.recommendation },
              { icon: <IcoSparkle />, color: BRAND, label: 'Opportunity', text: aiInsights.opportunity },
            ].map(({ icon, color, label, text }) => text && (
              <div key={label} style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: `${color}22`, border: `1px solid ${color}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0, marginTop: 1 }}>
                  {icon}
                </div>
                <div>
                  <p style={{ margin: '0 0 3px', fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</p>
                  <p style={{ margin: 0, fontSize: '0.83rem', color: 'var(--text-secondary)', lineHeight: 1.65 }}>{text}</p>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 4 }}>
              {[
                { val: timeRange.toUpperCase(), label: 'Period' },
                { val: `${growth.direction === 'up' ? '↑' : growth.direction === 'down' ? '↓' : '→'} ${growth.rate.toFixed(1)}%`, label: 'Trend', color: growthColor },
                { val: dealTypeData.length, label: 'Categories' },
              ].map(({ val, label, color }) => (
                <div key={label} style={{ textAlign: 'center', flex: 1 }}>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: color || 'var(--text-primary)', marginBottom: 2 }}>{val}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.83rem', margin: 0 }}>AI insights will appear once your earnings data is analyzed.</p>
        )}
      </div>
    </div>
  );
};

export default EarningsAnalytics;
